import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authService, RegisterRequest } from '@shared/services/api';
import AuthLayout from '@shared/layouts/AuthLayout';
import { Card } from '@shared/components/ui/Card';
import { Button } from '@shared/components/ui/Button';
import { Input } from '@shared/components/ui/Input';
import {
  PasswordRequirements,
  getPasswordValidation,
} from '@shared/components/PasswordRequirements';

export default function Register() {
  const { t } = useTranslation(['auth', 'errors', 'validation', 'common']);
  const [formData, setFormData] = useState<RegisterRequest>({
    email: '',
    password: '',
    full_name: '',
    accept_terms_of_service: false,
    accept_privacy_policy: false,
    accept_marketing: false,
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [nameTouched, setNameTouched] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);
  const [nameError, setNameError] = useState('');
  const [emailError, setEmailError] = useState('');

  const NAME_MIN_LENGTH = 3;
  const NAME_MAX_LENGTH = 50;

  // Once a field is touched, an empty value is "required" — not "no error yet".
  // Returning '' for empty input made the required message unreachable after the
  // user cleared a field, and let an empty value through on submit.
  const validateName = (name: string): string => {
    const trimmedName = name.trim();
    if (trimmedName.length === 0) return t('validation:nameRequired');
    if (trimmedName.length < NAME_MIN_LENGTH)
      return t('validation:nameTooShort', { min: NAME_MIN_LENGTH });
    if (trimmedName.length > NAME_MAX_LENGTH)
      return t('validation:nameTooLong', { max: NAME_MAX_LENGTH });
    return '';
  };

  const validateEmail = (email: string): string => {
    const trimmedEmail = email.trim();
    if (trimmedEmail.length === 0) return t('validation:emailRequired');
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmedEmail)) return t('validation:emailInvalid');
    return '';
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (loading) return;
    setError('');

    const nameValidationError = validateName(formData.full_name);
    if (nameValidationError) {
      setNameError(nameValidationError);
      setNameTouched(true);
      return;
    }

    const emailValidationError = validateEmail(formData.email);
    if (emailValidationError) {
      setEmailError(emailValidationError);
      setEmailTouched(true);
      return;
    }

    const passwordValidation = getPasswordValidation(formData.password);
    if (!passwordValidation.isValid) {
      setError(t('errors.passwordTooWeak'));
      return;
    }

    setLoading(true);
    try {
      await authService.register(formData);
      setSuccess(true);
    } catch (err) {
      const response =
        err && typeof err === 'object' && 'response' in err
          ? (err.response as { status?: number; data?: { error?: unknown } })
          : undefined;
      const serverError =
        typeof response?.data?.error === 'string' ? response.data.error.toLowerCase() : '';
      const emailAlreadyRegistered =
        response?.status === 409 || /email.*(already|exist|registered)/.test(serverError);

      setError(emailAlreadyRegistered ? t('errors.emailExists') : t('common:errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <AuthLayout>
        <div role="status" aria-live="polite" aria-labelledby="register-success-title">
          <Card className="p-8 text-center">
            <div className="mx-auto flex items-center justify-center h-20 w-20 rounded-2xl bg-state-ok-bg-light dark:bg-state-ok-bg-dark mb-8 border border-state-ok-light/20 dark:border-state-ok-dark/20">
              <svg
                className="h-10 w-10 text-state-ok-light dark:text-state-ok-dark"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={3}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h2
              id="register-success-title"
              className="text-3xl font-black text-slate-900 dark:text-white mb-2 tracking-tight"
            >
              {t('verifyEmail.successTitle')}
            </h2>
            <p className="text-text-muted-light dark:text-text-muted-dark mb-8 font-medium">
              {t('register.successMessage', { email: formData.email })}
            </p>
            <div className="mt-10">
              <Link
                to="/login"
                className="text-xs font-black uppercase tracking-widest text-brand-600 dark:text-brand-400 hover:underline"
              >
                {t('verifyEmail.backToLogin')}
              </Link>
            </div>
          </Card>
        </div>
      </AuthLayout>
    );
  }

  const passwordValidation = getPasswordValidation(formData.password);

  return (
    <AuthLayout>
      <Card className="p-8">
        <div className="text-center mb-10">
          <h2 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight">
            {t('register.title')}
          </h2>
          <p className="mt-2 text-sm text-text-muted-light dark:text-text-muted-dark font-medium">
            {t('register.subtitle')}
          </p>
        </div>

        <form className="space-y-5" onSubmit={handleSubmit}>
          {error && (
            <div
              role="alert"
              aria-live="assertive"
              className="bg-state-danger-bg-light dark:bg-state-danger-bg-dark border-l-4 border-state-danger-light dark:border-state-danger-dark p-4 rounded-xl"
            >
              <p className="text-sm font-bold text-state-danger-light dark:text-state-danger-dark">
                {error}
              </p>
            </div>
          )}

          <div className="space-y-4">
            <Input
              label={t('register.nameLabel')}
              id="full_name"
              name="full_name"
              type="text"
              required
              value={formData.full_name}
              disabled={loading}
              onChange={(e) => {
                setFormData({ ...formData, full_name: e.target.value });
                setNameTouched(true);
                setNameError(validateName(e.target.value));
              }}
              error={nameTouched ? nameError : undefined}
              placeholder={t('register.namePlaceholder')}
            />

            <Input
              label={t('register.emailLabel')}
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              value={formData.email}
              disabled={loading}
              onChange={(e) => {
                setFormData({ ...formData, email: e.target.value });
                setEmailTouched(true);
                setEmailError(validateEmail(e.target.value));
              }}
              error={emailTouched ? emailError : undefined}
              placeholder={t('register.emailPlaceholder')}
            />

            <div className="space-y-2">
              <Input
                label={t('register.passwordLabel')}
                id="password"
                name="password"
                type="password"
                autoComplete="new-password"
                required
                value={formData.password}
                disabled={loading}
                onChange={(e) => {
                  setFormData({ ...formData, password: e.target.value });
                  setPasswordTouched(true);
                }}
                placeholder={t('register.passwordPlaceholder')}
              />

              {passwordTouched && formData.password.length > 0 && (
                <PasswordRequirements
                  password={formData.password}
                  translationKeyPrefix="register.passwordRequirements"
                />
              )}
            </div>
          </div>

          <div className="space-y-4 py-4 border-t border-surface-border-light dark:border-surface-border-dark">
            <ConsentCheckbox
              id="accept_terms_of_service"
              checked={formData.accept_terms_of_service}
              disabled={loading}
              onChange={(val) => setFormData({ ...formData, accept_terms_of_service: val })}
              prefix={t('register.termsPrefix')}
              linkText={t('register.termsLink')}
              linkTo="/terms"
            />
            <ConsentCheckbox
              id="accept_privacy_policy"
              checked={formData.accept_privacy_policy}
              disabled={loading}
              onChange={(val) => setFormData({ ...formData, accept_privacy_policy: val })}
              prefix={t('register.privacyPrefix')}
              linkText={t('register.privacyLink')}
              linkTo="/privacy"
            />
          </div>

          <Button
            type="submit"
            className="w-full py-4 shadow-none disabled:bg-surface-2-light disabled:text-text-muted-light disabled:opacity-100 dark:disabled:bg-surface-2-dark dark:disabled:text-text-muted-dark"
            isLoading={loading}
            disabled={
              !passwordValidation.isValid ||
              !formData.accept_terms_of_service ||
              !formData.accept_privacy_policy
            }
          >
            {loading ? t('register.submitting') : t('register.submitButton')}
          </Button>

          <p className="text-center text-sm font-medium text-text-muted-light dark:text-text-muted-dark pt-2">
            {t('register.hasAccount')}{' '}
            <Link
              to="/login"
              className="font-black text-brand-600 dark:text-brand-400 hover:underline transition-colors"
            >
              {t('register.signIn')}
            </Link>
          </p>
        </form>
      </Card>
    </AuthLayout>
  );
}

function ConsentCheckbox({
  id,
  checked,
  disabled,
  onChange,
  prefix,
  linkText,
  linkTo,
}: {
  id: string;
  checked: boolean;
  disabled: boolean;
  onChange: (val: boolean) => void;
  prefix: string;
  linkText: string;
  linkTo: string;
}) {
  return (
    <div className="group flex min-h-11 items-start gap-3">
      <input
        id={id}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        aria-labelledby={`${id}-prefix ${id}-link`}
        className="mt-1 h-5 w-5 shrink-0 cursor-pointer accent-brand-600"
      />
      <div className="text-xs font-medium leading-relaxed text-text-muted-light transition-colors group-hover:text-slate-700 dark:text-text-muted-dark dark:group-hover:text-slate-300">
        <label htmlFor={id} id={`${id}-prefix`} className="cursor-pointer">
          {prefix}{' '}
        </label>
        <Link
          to={linkTo}
          id={`${id}-link`}
          className="font-bold text-brand-600 hover:underline dark:text-brand-400"
        >
          {linkText}
        </Link>{' '}
        <span className="text-state-danger-light dark:text-state-danger-dark">*</span>
      </div>
    </div>
  );
}
